import * as React from 'react'

import { action, computed, makeObservable, observable } from 'mobx'
import { inject, observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import { IUserProject } from '~/client/graph'
import { Loader } from '~/client/src/shared/components/Loader'
import { Checkbox } from '~/client/src/shared/components/shadcn-ui/checkbox'
import { IUserNotificationsSettings } from '~/client/src/shared/enums/ProjectSpecificUserProps'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import UserProjectsStore from '~/client/src/shared/stores/domain/UserProjects.store'
import * as e from '~/client/src/shared/stores/EventStore/eventConstants'

import EventsStore from '../../stores/EventStore/Events.store'

interface IProps {
  className?: string

  eventsStore?: EventsStore
  userProjectsStore?: UserProjectsStore
}

enum notificationsType {
  sms = 'sms',
  inApp = 'inApp',
  email = 'email',
}

const CHANNEL_LABELS: { [key in notificationsType]: () => string } = {
  [notificationsType.inApp]: () => Localization.translator.inApp,
  [notificationsType.sms]: () => Localization.translator.bySms,
  [notificationsType.email]: () => Localization.translator.byEmail,
}

const WORKFLOW_CHANNELS = [
  notificationsType.inApp,
  notificationsType.sms,
  notificationsType.email,
]
const IN_APP_AND_SMS = [notificationsType.inApp, notificationsType.sms]

// Every row lays out these columns so checkboxes line up across rows. Each row's channels must be a
// prefix of this: an empty cell stays at minimum width, so a skipped middle channel misaligns the rest.
const CHANNEL_COLUMNS = WORKFLOW_CHANNELS

const relatedEvents = [
  e.GET_CURRENT_USER,
  e.GET_AUTH_USER_PROJECT,
  e.SAVE_USER_PROJECTS,
]

// localization: translated

// eslint-disable-next-line
@inject('eventsStore', 'userProjectsStore')
@observer
export default class NotificationsSettingsControls extends React.Component<IProps> {
  @observable private localProps: IProps = null
  public constructor(props: IProps) {
    super(props)
    this.localProps = props
    makeObservable(this)
  }

  private get isLoading(): boolean {
    const { loading } = this.localProps.eventsStore.appState
    return relatedEvents.some(e => loading.get(e))
  }

  public render() {
    const { className } = this.localProps

    return (
      <div
        className={classList({
          'notifications-settings-controls': true,
          [className]: !!className,
        })}
      >
        {this.renderBody()}
      </div>
    )
  }

  private renderBody() {
    if (this.isLoading) return <Loader />

    const { workflowNotifications, scannerNotifications } =
      this.userActiveProjectSettings

    return (
      <div>
        {this.renderNotificationsSettingRow(
          Localization.translator.formNotifications,
          workflowNotifications,
          WORKFLOW_CHANNELS,
        )}
        {this.renderNotificationsSettingRow(
          Localization.translator.scannerNotifications,
          scannerNotifications,
          IN_APP_AND_SMS,
        )}
      </div>
    )
  }

  private renderNotificationsSettingRow(
    sectionTitle: string,
    notificationsSettings: IUserNotificationsSettings,
    channels: notificationsType[],
  ) {
    return (
      <>
        <div className="text mt20 extra-large bold capitalize">
          {sectionTitle}
        </div>
        {/* Not the legacy `row` class: its `.row > *` `flex: 1` overrides the cell widths. */}
        <div className="tw:flex tw:flex-wrap tw:items-center tw:pb-5">
          {/* Full width, so the channels always start on their own line at the same x. */}
          <div className="tw:w-full tw:whitespace-nowrap tw:text-body-md tw:text-foreground">
            {Localization.translator.iWouldLikeReceiveMy + ' ' + sectionTitle}
          </div>
          {/* Wraps instead of fixed widths so it fits the mobile settings screen. The cell width is
              a minimum so a long translated label grows its column instead of wrapping. */}
          <div className="tw:flex tw:flex-wrap tw:items-center">
            {CHANNEL_COLUMNS.map(channel => (
              <div
                key={channel}
                className="tw:flex tw:min-w-28 tw:shrink-0 tw:items-center"
              >
                {channels.includes(channel) && (
                  <>
                    <span className="tw:whitespace-nowrap tw:capitalize tw:text-body-md tw:text-foreground">
                      {CHANNEL_LABELS[channel]()}
                    </span>
                    <span className="tw:pl-2.5">
                      <Checkbox
                        checked={!!notificationsSettings?.[channel]}
                        onCheckedChange={() =>
                          this.toggleNotificationSettings(
                            notificationsSettings,
                            channel,
                          )
                        }
                      />
                    </span>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      </>
    )
  }

  @action.bound
  private toggleNotificationSettings(
    section: IUserNotificationsSettings,
    option: notificationsType,
  ) {
    // toggle
    section[option] = !section[option]

    this.localProps.userProjectsStore.save([this.userActiveProjectSettings])
  }

  @computed
  private get userActiveProjectSettings(): IUserProject {
    return this.localProps.eventsStore.appState.userActiveProjectSettings.toDto()
  }
}
